import React from 'react';
import { PeerDetailLite } from '../hooks/api/usePeers';
import { ScheduleCTA } from './schedule-cta';
import { FollowPeerStarButton } from './favorite-buttons/follow-star-button';
import { PhoneGrey26 } from '../assets/images/svgs';
import { PeersonaTile } from './peersona/peersona-tile';
import { usePeersonaAccess } from '../hooks/utils/usePeersonaAccess';
import useAuth from '../hooks/utils/useAuth';
import { AffiliationBanner, getAffiliationClientName } from './affiliation-banner';

interface PeerDetailsHeroProps {
  peerDetails: PeerDetailLite;
  onScheduleClick: () => void;
  onSeeMoreTimesClick: () => void;
  onHowDoesCallWorkClick: () => void;
  onSeeMoreListenersClick?: () => void;
  nextAvailableTime: string;
  className?: string;
}

export const PeerDetailsHero: React.FC<PeerDetailsHeroProps> = ({
  peerDetails,
  onScheduleClick,
  onSeeMoreTimesClick,
  onHowDoesCallWorkClick,
  onSeeMoreListenersClick,
  nextAvailableTime,
  className = '',
}) => {
  // Combine all traits for display
  const allTraits = [...(peerDetails.background_traits || []), ...(peerDetails.profile_traits || [])];

  // Determine availability states by checking the availabilities array
  const hasNoAvailabilities = !peerDetails.availabilities || peerDetails.availabilities.length === 0;
  const hasNoAvailability = hasNoAvailabilities && !peerDetails.is_available;
  const onCallButNoAvailability = peerDetails.is_available && hasNoAvailabilities;
  const { isPeerPeersonaEnabled } = usePeersonaAccess();
  const showPeersona = isPeerPeersonaEnabled(peerDetails.id, peerDetails.chatbot_id);
  const { user } = useAuth();
  const affiliationName = getAffiliationClientName(peerDetails, user?.client_id);

  return (
    <div className={`bg-peer-details-bg/25 py-12 md:py-16 ${className}`}>
      <div className="max-w-page mx-auto w-full px-6">
        <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:gap-8">
          {/* Left Section: Profile Photo — circle clips affiliation strip edges */}
          <div className="flex w-full flex-shrink-0 justify-center md:w-auto md:justify-start">
            <div className="relative h-[200px] w-[200px] overflow-hidden rounded-full border border-slate-300 md:h-[270px] md:w-[270px]">
              {peerDetails.profile_photo_url_square ? (
                <img
                  src={peerDetails.profile_photo_url_square}
                  alt={peerDetails.user.display_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-200">
                  <span className="text-4xl font-semibold text-gray-600">
                    {peerDetails.user.display_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {affiliationName && <AffiliationBanner clientName={affiliationName} variant="avatar-bottom" />}
            </div>
          </div>

          {/* Center Section: Name, Badges, About Me, Tags */}
          <div className="flex w-full flex-1 flex-col items-center gap-2 md:items-start md:gap-0">
            {/* LISTENER / PEERSONA Badges */}
            {(peerDetails.can_take_calls || showPeersona) && (
              <div className="mb-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 md:mb-0 md:justify-start">
                {peerDetails.can_take_calls && (
                  <div className="flex items-center gap-2">
                    <div className="flex h-4 w-4 items-center justify-center">
                      <img src={PhoneGrey26} alt="Listener" className="h-4 w-4" />
                    </div>
                    <span className="text-primary font-open-sans text-sm font-semibold leading-5 tracking-[0.48px] md:text-base md:leading-6">
                      PEER LISTENER
                    </span>
                  </div>
                )}
                {showPeersona && (
                  <span
                    className="font-open-sans bg-[#6B6FFF]/12 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#5B5FE5]"
                    title="This peer has a Peersona you can talk with"
                  >
                    <span aria-hidden>✶</span>
                    Peersona
                  </span>
                )}
              </div>
            )}

            {/* Name and Follow Button */}
            <div className="mb-4 flex items-center justify-center gap-4 md:mb-0 md:justify-start md:gap-2">
              <h1 className="font-albert-sans mr-4 text-center text-2xl font-semibold leading-9 text-black md:text-left md:text-3xl md:leading-[60px]">
                {peerDetails.user.display_name}
              </h1>
              <FollowPeerStarButton
                isFavorite={Boolean(peerDetails.favorite_id)}
                listenerRoleId={peerDetails.id}
                listenerName={peerDetails.user.display_name}
                className="h-12 w-12"
              />
            </div>

            {/* About Me */}
            {peerDetails.about_me && (
              <div className="mb-4 w-full max-w-[592px] py-2.5 text-center md:mb-6 md:text-left">
                <p className="font-open-sans text-base leading-7 text-black">{peerDetails.about_me}</p>
              </div>
            )}

            {/* Tags/Badges */}
            {allTraits.length > 0 && (
              <div className="mb-6 flex flex-wrap justify-center gap-2 md:mb-8 md:justify-start">
                {allTraits.map((trait, index) => (
                  <div
                    key={`${trait}-${index}`}
                    className="bg-pill inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
                  >
                    <span className="font-open-sans text-primary-dark text-sm font-medium leading-4">{trait}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Section: Schedule CTA + Peersona CTA */}
          {(peerDetails.can_take_calls || showPeersona) && (
            <div className="flex w-full flex-shrink-0 justify-center md:w-auto md:justify-start">
              <div className="flex w-full flex-col gap-4 px-4 md:w-[301px] md:px-0">
                {peerDetails.can_take_calls && (
                  <ScheduleCTA
                    peerName={peerDetails.user.display_name}
                    nextAvailableTime={nextAvailableTime}
                    availableNow={peerDetails.is_available}
                    hasNoAvailability={hasNoAvailability}
                    onCallButNoAvailability={onCallButNoAvailability}
                    onScheduleClick={onScheduleClick}
                    onSeeMoreTimesClick={onSeeMoreTimesClick}
                    onHowDoesCallWorkClick={onHowDoesCallWorkClick}
                    onSeeMoreListenersClick={onSeeMoreListenersClick}
                  />
                )}
                {showPeersona && peerDetails.chatbot_id && (
                  <PeersonaTile
                    chatbotId={peerDetails.chatbot_id}
                    peerName={peerDetails.user.display_name}
                    avatarUrl={peerDetails.profile_photo_url_square}
                    variant="profile"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
